/**
 * Performance budgets (§9). Acceptance criteria, not aspirations.
 *
 * They live in `contracts` rather than in the CI script so the runtime stats
 * HUD, the engine's adaptive-quality logic and `tools/check-budgets.ts` all
 * read the same numbers. A budget that CI enforces but the engine cannot see
 * is a budget the engine cannot help you meet.
 */

export const MB = 1024 * 1024;

export const BUDGETS = {
  /** Everything fetched before the masterplan is interactive, compressed. */
  masterplanPayloadBytes: 6 * MB,
  /** Additional payload for opening one unit, compressed. */
  unitPayloadBytes: 8 * MB,
  /** Throttled 4G, mid-range Android. */
  timeToInteractiveMs: 3000,
  frameRateTarget: 60,
  /** Hard floor. Sustained breach triggers the quality ladder. */
  frameRateFloor: 30,
  drawCallsMasterplan: 150,
  drawCallsUnit: 250,
  visibleTriangles: 1_500_000,
  textureMemoryBytes: 256 * MB,
} as const;

export type BudgetKey = keyof typeof BUDGETS;

export interface BudgetCheck {
  key: BudgetKey;
  label: string;
  limit: number;
  actual: number;
  /** Most budgets are ceilings; frame rate is a floor. */
  direction: 'max' | 'min';
  unit: 'bytes' | 'ms' | 'fps' | 'count';
  pass: boolean;
}

const META: Readonly<
  Record<BudgetKey, { label: string; direction: 'max' | 'min'; unit: BudgetCheck['unit'] }>
> = {
  masterplanPayloadBytes: {
    label: 'Masterplan initial payload',
    direction: 'max',
    unit: 'bytes',
  },
  unitPayloadBytes: { label: 'Unit payload', direction: 'max', unit: 'bytes' },
  timeToInteractiveMs: {
    label: 'Time to interactive (throttled 4G)',
    direction: 'max',
    unit: 'ms',
  },
  frameRateTarget: { label: 'Frame rate target', direction: 'min', unit: 'fps' },
  frameRateFloor: { label: 'Frame rate floor', direction: 'min', unit: 'fps' },
  drawCallsMasterplan: {
    label: 'Draw calls, masterplan',
    direction: 'max',
    unit: 'count',
  },
  drawCallsUnit: { label: 'Draw calls, unit', direction: 'max', unit: 'count' },
  visibleTriangles: {
    label: 'Visible triangles',
    direction: 'max',
    unit: 'count',
  },
  textureMemoryBytes: {
    label: 'Texture memory',
    direction: 'max',
    unit: 'bytes',
  },
};

export function checkBudget(key: BudgetKey, actual: number): BudgetCheck {
  const meta = META[key];
  const limit = BUDGETS[key];
  const pass = meta.direction === 'max' ? actual <= limit : actual >= limit;
  return { key, limit, actual, pass, ...meta };
}

export function formatBudgetValue(
  value: number,
  unit: BudgetCheck['unit'],
): string {
  switch (unit) {
    case 'bytes':
      return value >= MB
        ? `${(value / MB).toFixed(2)} MB`
        : `${(value / 1024).toFixed(0)} KB`;
    case 'ms':
      return `${Math.round(value)} ms`;
    case 'fps':
      return `${value.toFixed(0)} fps`;
    case 'count':
      return value.toLocaleString('en-US');
  }
}
